import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { savedApi } from '../../api/savedApi';
import SafeImage from '../../components/SafeImage';
import { Alert, Button, EmptyState, LoadingState } from '../../components/ui';
import { JOURNEY_CREATE_PATH } from '../my-journeys/myTripsIaNav';
import {
  SAVED_CONTENT_FILTERS,
  appendSavedContentItems,
  contentTypeLabel,
  emptyStateKey,
  itemKey,
  itemRoute,
  normalizeSavedContentPage,
  savedContentErrorMessage,
} from './savedModel';
import './saved.css';

function formatSavedAt(value, locale) {
  if (!value) return '';
  try {
    const tag = locale?.replace('_', '-') || undefined;
    return new Intl.DateTimeFormat(tag, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
  } catch {
    return '';
  }
}

function parseContentTypeFilter(raw) {
  const value = (raw || '').trim().toUpperCase();
  if (value === SAVED_CONTENT_FILTERS.SOCIAL_POST) return SAVED_CONTENT_FILTERS.SOCIAL_POST;
  if (value === SAVED_CONTENT_FILTERS.JOURNEY_PUBLICATION) return SAVED_CONTENT_FILTERS.JOURNEY_PUBLICATION;
  return SAVED_CONTENT_FILTERS.ALL;
}

export default function SavedPage() {
  const { locale, t, tf } = useTravelTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const contentTypeFilter = useMemo(
    () => parseContentTypeFilter(searchParams.get('contentType')),
    [searchParams],
  );
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const generation = useRef(0);

  const setFilter = useCallback((filter) => {
    const next = new URLSearchParams(searchParams);
    if (filter === SAVED_CONTENT_FILTERS.ALL) {
      next.delete('contentType');
    } else {
      next.set('contentType', filter);
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const load = useCallback(async ({ append = false, cursor = null, filter = contentTypeFilter } = {}) => {
    const requestGeneration = generation.current;
    setPhase(append ? 'loading-more' : 'loading');
    setError('');
    try {
      const params = { limit: 20 };
      if (filter !== SAVED_CONTENT_FILTERS.ALL) params.contentType = filter;
      if (cursor?.cursorUpdatedAt) params.cursorUpdatedAt = cursor.cursorUpdatedAt;
      if (cursor?.cursorSaveId != null) params.cursorSaveId = cursor.cursorSaveId;
      const page = normalizeSavedContentPage(await savedApi.list(params), t);
      if (requestGeneration !== generation.current) return;
      setItems((current) => (append ? appendSavedContentItems(current, page.items) : page.items));
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
      setPhase('ready');
    } catch (cause) {
      if (requestGeneration !== generation.current) return;
      setError(savedContentErrorMessage(cause, t));
      setPhase('error');
    }
  }, [contentTypeFilter, t]);

  useEffect(() => {
    generation.current += 1;
    setItems([]);
    setNextCursor(null);
    setHasMore(false);
    load({ filter: contentTypeFilter });
  }, [contentTypeFilter, load]);

  const emptyKey = emptyStateKey(contentTypeFilter);
  const showJourneyNextSteps = contentTypeFilter === SAVED_CONTENT_FILTERS.JOURNEY_PUBLICATION
    || items.some((item) => item.contentType === SAVED_CONTENT_FILTERS.JOURNEY_PUBLICATION);

  return (
    <main className="saved-hub" aria-labelledby="saved-hub-title">
      <header className="saved-hub__intro">
        <p>{t('travel.common.travel')}</p>
        <p className="saved-hub__owner">
          <Link to="/me" data-testid="saved-hub-me-owner">{t('travel.nav.me')}</Link>
          <span aria-hidden="true"> · </span>
          <span>{t('travel.nav.saved')}</span>
        </p>
        <h1 id="saved-hub-title">{t('travel.saved.title')}</h1>
        <span>{t('travel.saved.subtitle')}</span>
        <div className="saved-hub__create">
          <Link
            to={JOURNEY_CREATE_PATH}
            className="saved-hub__create-link"
            data-testid="saved-create-blank-journey"
          >
            {t('travel.saved.createBlankJourney')}
          </Link>
        </div>
      </header>

      <div className="saved-hub__filters" role="tablist" aria-label={t('travel.saved.filterLabel')}>
        {[
          { id: SAVED_CONTENT_FILTERS.ALL, label: t('travel.saved.filterAll') },
          { id: SAVED_CONTENT_FILTERS.SOCIAL_POST, label: t('travel.saved.filterPosts') },
          { id: SAVED_CONTENT_FILTERS.JOURNEY_PUBLICATION, label: t('travel.saved.filterJourneys') },
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={contentTypeFilter === tab.id}
            className={contentTypeFilter === tab.id ? 'saved-hub__filter saved-hub__filter--active' : 'saved-hub__filter'}
            onClick={() => setFilter(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {showJourneyNextSteps ? (
        <aside className="saved-hub__next-steps" data-testid="saved-journey-next-steps" aria-label={t('travel.saved.journeyNextStepsLabel')}>
          <p className="saved-hub__next-steps-title">{t('travel.saved.journeyNextStepsLabel')}</p>
          <p>{t('travel.saved.journeyNextStepsHint')}</p>
        </aside>
      ) : null}

      {phase === 'loading' ? (
        <section className="saved-hub__state">
          <LoadingState label={t('travel.saved.loading')} />
        </section>
      ) : null}

      {phase === 'error' && items.length === 0 ? (
        <section className="saved-hub__state saved-hub__state--error">
          <Alert
            variant="error"
            title={t('travel.saved.loadFailedTitle')}
            actions={
              <Button type="button" variant="secondary" onClick={() => load()}>
                {t('travel.common.retry')}
              </Button>
            }
          >
            {error}
          </Alert>
        </section>
      ) : null}

      {phase !== 'loading' && phase !== 'error' && items.length === 0 ? (
        <EmptyState
          className="saved-hub__state"
          title={t(`travel.saved.empty${emptyKey.charAt(0).toUpperCase()}${emptyKey.slice(1)}Title`)}
          description={t(`travel.saved.empty${emptyKey.charAt(0).toUpperCase()}${emptyKey.slice(1)}Hint`)}
          action={(
            <div className="saved-hub__empty-actions">
              <Link to="/explore">{t('travel.saved.explore')}</Link>
              {contentTypeFilter !== SAVED_CONTENT_FILTERS.SOCIAL_POST ? (
                <Link to={JOURNEY_CREATE_PATH} data-testid="saved-empty-create-journey">
                  {t('travel.saved.createBlankJourney')}
                </Link>
              ) : null}
            </div>
          )}
        />
      ) : null}

      {items.length > 0 ? (
        <ul className="saved-hub__list">
          {items.map((item) => (
            <li key={itemKey(item)}>
              <article className="saved-hub__card">
                <Link to={itemRoute(item)} className="saved-hub__card-main">
                  <SafeImage
                    src={item.coverUrl}
                    alt=""
                    fallback={<span className="saved-hub__fallback" aria-hidden="true">✦</span>}
                  />
                  <div>
                    <p className="saved-hub__type">{contentTypeLabel(item.contentType, t)}</p>
                    <h2>{item.title}</h2>
                    {item.savedAt ? (
                      <time dateTime={item.savedAt}>
                        {tf('travel.saved.savedAt', { time: formatSavedAt(item.savedAt, locale) })}
                      </time>
                    ) : null}
                  </div>
                </Link>
                {item.creatorDisplayName ? (
                  item.creatorId ? (
                    <Link
                      to={`/social/creators/${item.creatorId}`}
                      className="saved-hub__creator-link"
                    >
                      {item.creatorDisplayName}
                    </Link>
                  ) : (
                    <p className="saved-hub__creator">{item.creatorDisplayName}</p>
                  )
                ) : null}
                {item.contentType === SAVED_CONTENT_FILTERS.JOURNEY_PUBLICATION ? (
                  <div
                    className="saved-hub__card-actions"
                    role="group"
                    aria-label={t('travel.saved.cardActionsLabel')}
                    data-testid={`saved-journey-actions-${item.targetId}`}
                  >
                    <Link
                      to={itemRoute(item)}
                      className="saved-hub__card-action"
                      data-testid={`saved-journey-view-${item.targetId}`}
                    >
                      {t('travel.saved.viewPublicJourney')}
                    </Link>
                    <Link
                      to={itemRoute(item, { intent: 'copy' })}
                      className="saved-hub__card-action saved-hub__card-action--primary"
                      data-testid={`saved-journey-plan-${item.targetId}`}
                    >
                      {t('travel.saved.startPlanningViaCopy')}
                    </Link>
                  </div>
                ) : null}
              </article>
            </li>
          ))}
        </ul>
      ) : null}

      {error && items.length > 0 ? (
        <Alert variant="error" className="saved-hub__inline-error">
          {error}
          <Button
            type="button"
            variant="secondary"
            onClick={() => load({ append: true, cursor: nextCursor, filter: contentTypeFilter })}
          >
            {t('travel.common.retry')}
          </Button>
        </Alert>
      ) : null}

      {hasMore ? (
        <div className="saved-hub__more">
          <Button
            type="button"
            variant="secondary"
            loading={phase === 'loading-more'}
            onClick={() => load({ append: true, cursor: nextCursor, filter: contentTypeFilter })}
          >
            {phase === 'loading-more' ? t('travel.common.loading') : t('travel.common.loadMore')}
          </Button>
        </div>
      ) : null}
    </main>
  );
}
